// Low-level helpers shared by the single-PUT and multipart upload paths.
// XMLHttpRequest rather than fetch: it is the only browser API that reports
// upload progress (xhr.upload.onprogress) and can be cancelled mid-body.

// Give up on a request only when no bytes have moved for this long. A fixed
// per-request timeout would kill slow-but-moving uploads on narrow uplinks;
// a stall detector makes a hung connection retryable at any speed.
export var DEFAULT_STALL_MS = 60000

export function uploadError(code, message, status) {
  var err = new Error(message || code)
  err.code = code
  if (status !== undefined) err.status = status
  return err
}

export function abortError() {
  var err = new Error("Upload cancelled")
  err.name = "AbortError"
  err.code = "aborted"
  return err
}

export function isAbortError(err) {
  return !!err && err.name === "AbortError"
}

// Network failure (0), request timeout, rate limiting and server errors are
// worth another try; any other 4xx (bad signature, expired URL, rejected
// request) is final.
export function isRetryableStatus(status) {
  return (
    status === 0 ||
    status === 408 ||
    status === 429 ||
    (status >= 500 && status <= 599)
  )
}

// 1s, 2s, 4s, 8s ... capped at 15s, plus up to 500ms of jitter so parallel
// parts that failed together do not retry in lockstep.
export function backoffMs(attempt, random) {
  var jitter = Math.floor((random || Math.random)() * 500)
  return Math.min(1000 * Math.pow(2, attempt), 15000) + jitter
}

// Resolves after `ms`, or rejects with an AbortError the moment `signal`
// fires, so a cancel during a retry backoff is honoured immediately.
export function delay(ms, signal) {
  return new Promise(function (resolve, reject) {
    if (signal && signal.aborted) {
      reject(abortError())
      return
    }
    function onAbort() {
      clearTimeout(timer)
      reject(abortError())
    }
    var timer = setTimeout(function () {
      if (signal) signal.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    if (signal) signal.addEventListener("abort", onAbort)
  })
}

// PUT `opts.body` to `url`. Resolves with the xhr on 2xx; rejects with an
// uploadError carrying `.status` (0 for network/stall/abort), or an
// AbortError when `opts.signal` fired. `register`/`unregister` let a caller
// track in-flight requests so it can cancel siblings on a fatal failure.
// `opts.stallMs` overrides DEFAULT_STALL_MS (0 disables the detector).
export function xhrPut(url, opts) {
  opts = opts || {}
  return new Promise(function (resolve, reject) {
    var signal = opts.signal
    if (signal && signal.aborted) {
      reject(abortError())
      return
    }

    var xhr = new XMLHttpRequest()
    var settled = false
    var stalled = false
    var stallMs = opts.stallMs === undefined ? DEFAULT_STALL_MS : opts.stallMs
    var stallTimer = null

    function onAbort() {
      xhr.abort()
    }

    function clearStall() {
      if (stallTimer) {
        clearTimeout(stallTimer)
        stallTimer = null
      }
    }

    function armStall() {
      clearStall()
      if (!stallMs) return
      stallTimer = setTimeout(function () {
        stalled = true
        xhr.abort()
      }, stallMs)
    }

    function finish(settle, value) {
      if (settled) return
      settled = true
      clearStall()
      if (signal) signal.removeEventListener("abort", onAbort)
      if (opts.unregister) opts.unregister(xhr)
      settle(value)
    }

    if (signal) signal.addEventListener("abort", onAbort)

    // Upload listeners must be attached before open() to fire in every browser.
    xhr.upload.onprogress = function (event) {
      armStall()
      if (event.lengthComputable && opts.onProgress) {
        opts.onProgress(event.loaded, event.total)
      }
    }
    xhr.open(opts.method || "PUT", url, true)
    var headers = opts.headers || {}
    Object.keys(headers).forEach(function (name) {
      xhr.setRequestHeader(name, headers[name])
    })
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        finish(resolve, xhr)
      } else {
        finish(
          reject,
          uploadError(
            "upload_failed",
            "Upload failed with status " + xhr.status,
            xhr.status
          )
        )
      }
    }
    xhr.onerror = function () {
      finish(reject, uploadError("network", "Network error during upload", 0))
    }
    xhr.onabort = function () {
      if (signal && signal.aborted) {
        finish(reject, abortError())
      } else if (stalled) {
        finish(
          reject,
          uploadError(
            "network",
            "Upload stalled: no progress for " + stallMs + "ms",
            0
          )
        )
      } else {
        finish(reject, uploadError("network", "Upload aborted", 0))
      }
    }

    if (opts.register) opts.register(xhr)
    armStall()
    xhr.send(opts.body)
  })
}
